/**
 * Cloud GM AI Control Hub — 3-tier navigation via URL search-params.
 *
 *   /control/ai                                  → Tier-1 (provider hub)
 *   /control/ai?provider=anthropic|google        → Tier-2 (sub-hub: Control / Usage)
 *   /control/ai?provider=...&view=control        → Tier-3 toggles
 *   /control/ai?provider=...&view=usage          → Tier-3 usage log
 *
 * Breadcrumb is built from the same URL, so the back button + header back
 * both behave naturally. No internal "back" buttons.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ProviderHubView } from '../components/ai-control/ProviderHubView';
import AiChatCostEstimator from '../components/ai-control/AiChatCostEstimator';
import { AiModelsDirectoryView } from '../components/ai-control/AiModelsDirectoryView';
import { ProviderSubHubView } from '../components/ai-control/ProviderSubHubView';
import { PROVIDER_META } from '../components/ai-control/constants';
import { UsageLogView } from '../components/ai-control/UsageLogView';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, KeyRound, Bot,
  BookMarked, Calculator, ChevronLeft, ChevronRight,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import BotTrainingStudio from '../components/BotTrainingStudio';
import TrainingLibrary from '../components/TrainingLibrary';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;



// =============================================================================
// Root — URL-driven 3-tier router
// =============================================================================
export default function AiControlCenter() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [params, setParams] = useSearchParams();

  const providerId = params.get('provider');       // 'anthropic' | 'google' | null
  const view = params.get('view');                  // 'control' | 'usage' | null
  // iter-218t (Feb 2026) — new top-level panels shown INSTEAD of the
  // provider hub when the admin taps one of the new header buttons.
  // Values: null | 'directory' | 'estimator'.
  const panel = params.get('panel');

  const [providers, setProviders] = useState([]);
  const [env, setEnv] = useState({ preview_locked: false, environment: 'production' });
  const [loading, setLoading] = useState(true);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/ai-control/providers`, { headers: getAuthHeaders() });
      setProviders(res.data?.providers || []);
      setEnv({
        preview_locked: !!res.data?.preview_locked,
        environment: res.data?.environment || 'production',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل التحميل' : 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  // Resolve selected provider from URL
  const selectedProvider = useMemo(() => {
    if (!providerId) return null;
    return providers.find((p) => p.id === providerId) || null;
  }, [providerId, providers]);

  // Breadcrumb is built from the URL so back-button + breadcrumb both work.
  const breadcrumb = useMemo(() => {
    const out = [];
    if (selectedProvider) {
      out.push({ labelAr: 'التحكم بالـ AI', labelEn: 'AI Control', path: '/control/ai' });
      if (view) {
        const providerLabelAr = language === 'ar' ? selectedProvider.label_ar : selectedProvider.label_en;
        out.push({
          labelAr: providerLabelAr,
          labelEn: providerLabelAr,
          path: `/control/ai?provider=${providerId}`,
        });
        const viewLabels = {
          usage:    { ar: 'سجل الاستخدام', en: 'Usage Log'        },
          bots:     { ar: 'إدارة الذكاء الاصطناعي', en: 'AI Management' },
          training: { ar: 'مكتبة التدريب',  en: 'Training Library' },
        };
        const vl = viewLabels[view] || viewLabels.bots;
        out.push({
          labelAr: vl.ar,
          labelEn: vl.en,
        });
      } else {
        const providerLabel = language === 'ar' ? selectedProvider.label_ar : selectedProvider.label_en;
        out.push({ labelAr: providerLabel, labelEn: providerLabel });
      }
    } else if (panel === 'directory') {
      out.push({ labelAr: 'التحكم بالـ AI', labelEn: 'AI Control', path: '/control/ai' });
      out.push({ labelAr: 'التحكم بنماذج الذكاء الاصطناعي', labelEn: 'AI Models Control' });
    } else if (panel === 'estimator') {
      out.push({ labelAr: 'التحكم بالـ AI', labelEn: 'AI Control', path: '/control/ai' });
      out.push({ labelAr: 'مقدّر تكلفة الذكاء الاصطناعي', labelEn: 'AI Cost Estimator' });
    } else {
      out.push({ labelAr: 'التحكم بالـ AI', labelEn: 'AI Control' });
    }
    return out;
  }, [selectedProvider, view, language, providerId, panel]);

  const subtitle = useMemo(() => {
    if (!selectedProvider) return language === 'ar' ? 'اختر مزوّد الذكاء الاصطناعي' : 'Pick an AI provider';
    if (view === 'usage') return language === 'ar' ? 'تتبّع كل نداء وتكلفته' : 'Track every call and its cost';
    if (view === 'bots') return language === 'ar' ? 'تحرير System Prompt والموديل لكل بوت' : 'Edit System Prompt & model per bot';
    if (view === 'training') return language === 'ar' ? 'رفع Excel وحقنه في البوتات كسياق تدريب' : 'Upload Excel & inject as training context';
    return language === 'ar' ? 'اختر: إدارة الذكاء الاصطناعي أو المكتبة أو السجل' : 'Pick: AI Management, Library or Log';
  }, [selectedProvider, view, language]);

  // iter-232b — Smart back-button target. Previously it always went to
  // /dashboard, jumping the operator out of the AI section entirely. Now
  // the target follows the URL depth:
  //   provider + view   → back to provider hub    (?provider=X)
  //   provider only     → back to AI Control root
  //   panel (directory / estimator) → back to AI Control root
  //   AI Control root   → back to /dashboard
  const backTarget = useMemo(() => {
    if (selectedProvider && view) return `/control/ai?provider=${providerId}`;
    if (selectedProvider || panel) return '/control/ai';
    return '/dashboard';
  }, [selectedProvider, view, providerId, panel]);

  const handleBack = () => navigate(backTarget);

  const selectProvider = (p) => setParams({ provider: p.id });
  const pickView = (v) => setParams({ provider: providerId, view: v });

  return (
    <DashboardLayout
      title="Cloud GM AI Control Hub"
      titleEn="Cloud GM AI Control Hub"
      subtitle={subtitle}
      subtitleEn={subtitle}
      screenCode="AI-001"
      backPath={backTarget}
      onBack={handleBack}
      breadcrumb={breadcrumb}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="ai-control-root">
        <div className="flex items-start justify-end flex-wrap gap-3">
          <span className="px-3 py-1.5 text-xs rounded-lg border font-mono flex items-center gap-2 bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
            data-testid="ai-env-badge"
          >
            <KeyRound className="h-3.5 w-3.5" />
            {env.environment === 'preview'
              ? (language === 'ar' ? 'بريفيو 🟢' : 'Preview 🟢')
              : (language === 'ar' ? 'إنتاج 🟢' : 'Production 🟢')}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#d4af37]" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {!selectedProvider && !panel && (
              <motion.div key="tier1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-5">
                {/* iter-218t (Feb 2026) — Two new top-level buttons replace
                    the always-visible cost estimator card. Each one opens a
                    dedicated panel via ?panel=… query-param so back/forward
                    and breadcrumbs work naturally. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setParams({ panel: 'directory' })}
                    className="group text-start rounded-2xl bg-gradient-to-br from-[#d4af37]/15 to-[#d4af37]/5 border border-[#d4af37]/40 hover:border-[#d4af37]/70 p-5 h-28 flex items-center justify-between transition-all"
                    data-testid="ai-btn-directory"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-[#d4af37]/20 border border-[#d4af37]/40 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <BookMarked className="w-7 h-7 text-[#d4af37]" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-white">
                          {language === 'ar' ? 'التحكم بنماذج الذكاء الاصطناعي' : 'AI Models Control'}
                        </h3>
                        <p className="text-[11px] text-white/60 mt-1">
                          {language === 'ar'
                            ? 'كل النماذج بأكوادها الرسمية — إضافة، تعديل، إلغاء'
                            : 'Every registered agent — add, edit, deprecate'}
                        </p>
                      </div>
                    </div>
                    {language === 'ar'
                      ? <ChevronLeft className="w-5 h-5 text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity" />
                      : <ChevronRight className="w-5 h-5 text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </button>

                  <button
                    onClick={() => setParams({ panel: 'estimator' })}
                    className="group text-start rounded-2xl bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border border-emerald-500/40 hover:border-emerald-500/70 p-5 h-28 flex items-center justify-between transition-all"
                    data-testid="ai-btn-estimator"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Calculator className="w-7 h-7 text-emerald-300" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-white">
                          {language === 'ar' ? 'مقدّر تكلفة الذكاء الاصطناعي' : 'AI Cost Estimator'}
                        </h3>
                        <p className="text-[11px] text-white/60 mt-1">
                          {language === 'ar'
                            ? 'حاسبة تكاليف المحادثات لكل مزود ونموذج'
                            : 'Conversation cost calculator per provider & model'}
                        </p>
                      </div>
                    </div>
                    {language === 'ar'
                      ? <ChevronLeft className="w-5 h-5 text-emerald-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                      : <ChevronRight className="w-5 h-5 text-emerald-300 opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </button>
                </div>

                <ProviderHubView
                  language={language}
                  providers={providers}
                  onSelect={selectProvider}
                  onRefresh={loadProviders}
                  onOpenArchives={() => navigate('/control/ai/moderation')}
                />
              </motion.div>
            )}
            {!selectedProvider && panel === 'directory' && (
              <motion.div key="panel-directory" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
                <AiModelsDirectoryView language={language} />
              </motion.div>
            )}
            {!selectedProvider && panel === 'estimator' && (
              <motion.div key="panel-estimator" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
                <AiChatCostEstimator language={language} />
              </motion.div>
            )}
            {selectedProvider && !view && (
              <motion.div key="tier2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
                <ProviderSubHubView
                  language={language}
                  provider={selectedProvider}
                  onPick={pickView}
                />
              </motion.div>
            )}
            {selectedProvider && view === 'bots' && (
              /* iter-232b — legacy `?view=bots` deep-link — redirect to the
                 AI Models Control page which now owns per-bot editing. */
              <Navigate to="/control/ai?panel=directory" replace />
            )}
            {selectedProvider && view === 'training' && (
              <motion.div key="tier3-training" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
                <TrainingLibrary language={language} providerFilter={selectedProvider.id} />
              </motion.div>
            )}
            {selectedProvider && view === 'usage' && (
              <motion.div key="tier3-usage" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
                <UsageLogView
                  language={language}
                  provider={selectedProvider}
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </section>
    </DashboardLayout>
  );
}
